// Speech providers through the Depth API (NARRATION.md "Requests", "Voice providers"; OPENAI.md §3; SERVER.md §2).
// Each call narrates one segment (≤700 chars) and returns 24 kHz s16le mono PCM.
import { api, errorBody, problem, proxyBase } from "../api/client";
import { modelNotAllowedMessage } from "../models";
import type { NarrationVoice } from "../settings";
import { isAbort } from "../util";

export const RATE = 24000;

export class TTSFailure extends Error {
  constructor(message: string, readonly kind: "network" | "rateLimited" | "daily" | "unavailable" | "fatal" | "filtered" | "rejected",
    readonly retryAfter = 30) { super(message); }
}

/** "Perform the transcript in the style of …" wrapper (GeminiTTS.prompt). */
export function geminiPrompt(text: string, style: string): string {
  const s = style.trim();
  if (!s) return text;
  return `## DIRECTOR'S NOTES\nPerform the transcript in the style of ${s}. Speak only the transcript.\n\n## TRANSCRIPT\n${text}`;
}

export function realtimeInstructions(style: string): string {
  const base = "You are a narrator. Read the user's text aloud exactly as written, word for word, in English. Do not add, skip, answer, summarize or comment on anything. Speak only the text.";
  return style.trim() ? `${base} Perform it in the style of ${style.trim()}.` : base;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concat(chunks: Uint8Array[]): Uint8Array {
  const n = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(n);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.length; }
  return out;
}

export function toInt16(bytes: Uint8Array): Int16Array {
  const even = bytes.length - (bytes.length % 2);
  const copy = bytes.slice(0, even);
  return new Int16Array(copy.buffer, copy.byteOffset, even / 2);
}

export function resample(pcm: Int16Array, from: number, to = RATE): Int16Array {
  if (from === to) return pcm;
  const n = Math.floor(pcm.length * to / from);
  const out = new Int16Array(n);
  for (let i = 0; i < n; i++) {
    const x = i * from / to, a = Math.floor(x), f = x - a;
    out[i] = Math.round((pcm[a] ?? 0) * (1 - f) + (pcm[a + 1] ?? pcm[a] ?? 0) * f);
  }
  return out;
}

async function serverFailure(res: Response, provider: string): Promise<TTSFailure> {
  const { json, text } = await errorBody(res);
  const p = problem(res.status, json);
  if (p?.kind === "signedOut") return new TTSFailure("Signed out. Sign in to use narration.", "fatal");
  if (p?.kind === "providerUnavailable") return new TTSFailure(`${provider} isn't available on this server.`, "fatal");
  if (p?.kind === "rateLimited") return new TTSFailure("Depth API limit reached", "rateLimited", Number(res.headers.get("retry-after")) || 30);
  if (res.status === 403 && json?.error === "model_not_allowed") return new TTSFailure(modelNotAllowedMessage(json.model), "fatal");
  const message = (json?.error?.message ?? text ?? "").toString().slice(0, 200);
  if (res.status === 429) {
    const details = json?.error?.details ?? [];
    const daily = details.some((d: any) => (d.violations ?? []).some((v: any) => String(v.quotaId ?? "").includes("PerDay"))) || /per-day|free-models-per-day/i.test(message);
    const delay = details.find((d: any) => d.retryDelay)?.retryDelay;
    const secs = delay ? parseFloat(delay) : Number(res.headers.get("retry-after")) || 30;
    return new TTSFailure(`${provider} rate limit: ${message}`, daily ? "daily" : "rateLimited", secs || 30);
  }
  if (res.status >= 500) return new TTSFailure(`${provider} unavailable (${res.status}): ${message}`, "unavailable");
  if (res.status === 402) return new TTSFailure(`${provider} credits used up`, "fatal");
  if (res.status === 401 || res.status === 403) return new TTSFailure(`${provider} rejected the server's key`, "fatal");
  if (res.status === 400 && provider === "OpenRouter" && /explicit voice is required/i.test(message)) return new TTSFailure(`Pick a voice in Settings (${message})`, "fatal");
  if (res.status === 400 && provider === "OpenRouter") return new TTSFailure(message, "rejected");
  return new TTSFailure(`${provider} error ${res.status}: ${message}`, "fatal");
}

async function* sseEvents(res: Response, signal: AbortSignal): AsyncGenerator<any> {
  const reader = res.body!.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.search(/\r?\n\r?\n/)) >= 0) {
      const block = buf.slice(0, i);
      buf = buf.slice(i).replace(/^\r?\n\r?\n/, "");
      const data = block.split(/\r?\n/).filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("");
      if (data) { try { yield JSON.parse(data); } catch { /* skip */ } }
    }
  }
}

export interface NarrateOptions { tier?: "flex" | "standard"; style: string; signal: AbortSignal; onFirstAudio?: () => void }

/** Gemini: streamGenerateContent?alt=sse with base64 PCM in inlineData. */
async function gemini(text: string, v: NarrationVoice, o: NarrateOptions): Promise<Int16Array> {
  const body: any = { contents: [{ parts: [{ text: geminiPrompt(text, o.style) }] }],
    generationConfig: { responseModalities: ["AUDIO"], speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: v.voice || "Charon" } } } } };
  if (o.tier === "flex") body.service_tier = "flex";
  const res = await api(`${proxyBase("gemini")}/v1beta/models/${v.model}:streamGenerateContent?alt=sse`, {
    method: "POST", json: body, headers: { Accept: "text/event-stream" }, signal: o.signal });
  if (!res.ok) throw await serverFailure(res, "Gemini");
  const chunks: Uint8Array[] = [];
  let finish: string | null = null, blocked: string | null = null;
  for await (const ev of sseEvents(res, o.signal)) {
    if (ev.promptFeedback?.blockReason) blocked = ev.promptFeedback.blockReason;
    for (const c of ev.candidates ?? []) {
      for (const p of c.content?.parts ?? []) {
        if (p.inlineData?.data) { if (!chunks.length) o.onFirstAudio?.(); chunks.push(b64ToBytes(p.inlineData.data)); }
      }
      if (c.finishReason) finish = c.finishReason;
    }
  }
  if (blocked || (finish && finish !== "STOP")) throw new TTSFailure(`Gemini declined to narrate this passage (${blocked ?? finish})`, "filtered");
  if (!finish) throw new TTSFailure("The stream ended early", "network");
  if (!chunks.length) throw new TTSFailure("Gemini returned no audio", "filtered");
  return toInt16(concat(chunks));
}

/** OpenRouter: /api/v1/audio/speech → raw PCM (audio/pcm;rate=N) or MP3. */
async function openrouter(text: string, v: NarrationVoice, o: NarrateOptions, mp3 = false): Promise<Int16Array> {
  const body: any = { model: v.model, input: text, response_format: mp3 ? "mp3" : "pcm" };
  if (v.voice) body.voice = v.voice;
  const deadline = AbortSignal.any([o.signal, AbortSignal.timeout(Math.min(120, Math.max(20, 6 + 0.04 * text.length)) * 1000)]);
  let res: Response;
  try {
    res = await api(`${proxyBase("openrouter")}/api/v1/audio/speech`, { method: "POST", json: body, signal: deadline });
  } catch (e) {
    if (o.signal.aborted) throw e;
    throw new TTSFailure("OpenRouter timed out", "network");
  }
  if (res.status === 400 && !mp3) {
    const { text: t } = await errorBody(res.clone());
    if (t.includes("response_format") && t.toLowerCase().includes("mp3")) return openrouter(text, v, o, true);
  }
  if (!res.ok) throw await serverFailure(res, "OpenRouter");
  o.onFirstAudio?.();
  const type = res.headers.get("content-type") || "";
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (type.includes("mpeg") || mp3) return decodeCompressed(bytes);
  const rate = parseInt(/rate=(\d+)/.exec(type)?.[1] ?? "24000", 10);
  return resample(toInt16(bytes), rate);
}

async function decodeCompressed(bytes: Uint8Array): Promise<Int16Array> {
  const ctx = new OfflineAudioContext(1, 1, RATE);
  const buf = await ctx.decodeAudioData(bytes.slice().buffer);
  const data = buf.getChannelData(0);
  const pcm = new Int16Array(data.length);
  for (let i = 0; i < data.length; i++) pcm[i] = Math.max(-32768, Math.min(32767, Math.round(data[i] * 32767)));
  return resample(pcm, buf.sampleRate);
}

/** OpenAI Realtime (OPENAI.md §3) from a browser: a client secret per segment from the Depth API, then OpenAI's
 *  documented browser WebSocket authentication (subprotocols "realtime" + "openai-insecure-api-key.<secret>"). */
async function openai(text: string, v: NarrationVoice, o: NarrateOptions): Promise<Int16Array> {
  const res = await api("/v1/openai/realtime/session", { method: "POST", json: { model: v.model }, signal: o.signal });
  if (!res.ok) throw await serverFailure(res, "OpenAI");
  const secret = await res.json();
  const url = `${secret.url}?model=${encodeURIComponent(v.model)}`;
  return new Promise<Int16Array>((resolve, reject) => {
    let ws: WebSocket;
    try { ws = new WebSocket(url, ["realtime", "openai-insecure-api-key." + secret.value]); } catch (e) { reject(new TTSFailure(String(e), "network")); return; }
    const chunks: Uint8Array[] = [];
    let transcript = "", settled = false, timer: ReturnType<typeof setTimeout>;
    const done = (err: Error | null, pcm?: Int16Array) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { ws.close(); } catch { /* closed */ }
      err ? reject(err) : resolve(pcm!);
    };
    const arm = (ms: number) => { clearTimeout(timer); timer = setTimeout(() => done(new TTSFailure("OpenAI Realtime timed out", "network")), ms); };
    arm(20_000);
    o.signal.addEventListener("abort", () => done(new DOMException("Aborted", "AbortError")), { once: true });
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "session.update", session: { type: "realtime", output_modalities: ["audio"], instructions: realtimeInstructions(o.style),
        audio: { output: { format: { type: "audio/pcm", rate: 24000 }, voice: v.voice || "marin" } } } }));
      ws.send(JSON.stringify({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{ type: "input_text", text }] } }));
      ws.send(JSON.stringify({ type: "response.create" }));
    };
    ws.onmessage = (m) => {
      let ev: any;
      try { ev = JSON.parse(String(m.data)); } catch { return; }
      if (ev.type === "response.output_audio.delta") { if (!chunks.length) o.onFirstAudio?.(); chunks.push(b64ToBytes(ev.delta)); arm(30_000); }
      else if (ev.type === "response.output_audio_transcript.delta") transcript += ev.delta ?? "";
      else if (ev.type === "error") {
        const e = ev.error ?? {};
        if (e.code === "invalid_api_key") done(new TTSFailure("OpenAI key rejected", "fatal"));
        else if (e.code === "insufficient_quota") done(new TTSFailure("OpenAI credits used up", "fatal"));
        else if (e.code === "rate_limit_exceeded") done(new TTSFailure(e.message ?? "rate limit", "rateLimited", parseFloat(/try again in ([\d.]+)s/.exec(e.message ?? "")?.[1] ?? "30")));
        else if (String(e.param ?? "").includes("voice")) done(new TTSFailure(`Pick a voice for ${v.model} in Settings`, "fatal"));
        else done(new TTSFailure(e.message ?? "OpenAI error", "fatal"));
      } else if (ev.type === "response.done") {
        const r = ev.response ?? {};
        if (r.status === "completed") done(null, toInt16(concat(chunks)));
        else if (r.status_details?.reason === "content_filter") done(new TTSFailure("OpenAI declined to narrate this passage", "filtered"));
        else done(new TTSFailure(`OpenAI response ${r.status}`, "network"));
      }
    };
    ws.onerror = () => done(new TTSFailure("OpenAI Realtime connection failed", "network"));
    ws.onclose = (e) => {
      if (e.code === 3000) done(new TTSFailure("OpenAI key rejected", "fatal"));
      else if (e.code === 4004) done(new TTSFailure(e.reason || "model not found", "fatal"));
      else done(new TTSFailure("OpenAI Realtime closed early", "network"));
    };
    void transcript;
  });
}

/** One segment with the given voice. Network errors surface as TTSFailure("network"). */
export async function narrate(text: string, v: NarrationVoice, o: NarrateOptions): Promise<Int16Array> {
  try {
    if (v.provider === "openrouter") return await openrouter(text, v, o);
    if (v.provider === "openai") return await openai(text, v, o);
    return await gemini(text, v, o);
  } catch (e) {
    if (e instanceof TTSFailure || isAbort(e)) throw e;
    throw new TTSFailure(e instanceof Error ? e.message : String(e), "network");
  }
}
